/**
 * CSV Helper - Shared functions for CSV import/export
 */

/**
 * Escapes double quotes in a CSV field value
 * @param {string} value - The value to escape
 * @returns {string} - The escaped value wrapped in quotes
 */
export const escapeCsvField = (value) => {
  return `"${(value || "").toString().replace(/"/g, '""')}"`;
};

/**
 * Exports dataset values to CSV file
 * @param {Object} options - Export options
 * @param {Array} options.values - Dataset values array
 * @param {string} options.datasetName - Name for the exported file
 * @param {Array<string>} options.headers - CSV headers
 * @param {boolean} options.hasSecondColumn - Whether to include second column (filterValue/parameterValue)
 * @param {string} options.secondColumnKey - Key for second column value (default: "filterValue")
 */
export const exportToCsvFile = ({
  values,
  datasetName,
  headers,
  hasSecondColumn,
  secondColumnKey = "filterValue",
}) => {
  const csvContent = [
    headers.join(","),
    ...values.map((row) => {
      const value = escapeCsvField(row.value);
      if (hasSecondColumn) {
        const secondValue = escapeCsvField(row[secondColumnKey]);
        return `${value},${secondValue}`;
      }
      return value;
    }),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${datasetName || "dataset"}_export.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Transforms CSV rows to dataset format
 * @param {Array<Array<string>>} rows - Raw CSV rows
 * @param {boolean} hasSecondColumn - Whether to include second column
 * @param {string} secondColumnKey - Key for second column (default: "filterValue")
 * @returns {Array<Object>} - Transformed dataset rows
 */
export const transformCsvRows = (rows, hasSecondColumn, secondColumnKey = "filterValue") => {
  return rows.map((row, index) => {
    const item = {
      index: index + 1,
      value: row[0] || "",
    };
    if (hasSecondColumn && row.length > 1) {
      item[secondColumnKey] = row[1] || "";
    }
    return item;
  });
};

/**
 * Validates CSV format based on header requirements
 * @param {Object} csvResult - Result from ReadCSV
 * @param {string} requiredHeaderKeyword - Keyword that must be in first header (case insensitive)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateCsvFormat = (csvResult, requiredHeaderKeyword) => {
  if (!csvResult?.rows?.length) {
    return { isValid: false, error: "empty_file" };
  }

  const headers = csvResult.headers || [];

  if (headers.length === 0) {
    return { isValid: false, error: "no_headers" };
  }

  const hasValidHeader =
    headers[0].toLowerCase().includes(requiredHeaderKeyword.toLowerCase());

  if (!hasValidHeader) {
    return { isValid: false, error: "invalid_header" };
  }

  const hasInvalidRows = csvResult.rows.some(
    (row) => row[0] === undefined || row[0] === ""
  );

  if (hasInvalidRows) {
    return { isValid: false, error: "empty_values" };
  }

  return { isValid: true, error: null };
};

/**
 * Checks if CSV has a second column with specific keyword in header
 * @param {Array<string>} headers - CSV headers
 * @param {string} keyword - Keyword to check in second header
 * @returns {boolean}
 */
export const hasSecondColumnWithKeyword = (headers, keyword) => {
  return (
    headers.length > 1 &&
    headers[1].toLowerCase().includes(keyword.toLowerCase())
  );
};
